<template>
  <div>
    <div v-if="totalReviews < 1">
      <div class="row">
        <div class="col">
          <div v-if="loadingReviews" class="text-center">
            <b-spinner></b-spinner>
          </div>
          <h3 v-else class="text-center mt-5 d-block"
            >We are currently waiting for your reviews</h3
          >
        </div>
      </div>
    </div>

    <div v-else class="row">
      <div class="col-12">
        <div
          v-if="!forDashboard"
          class="d-flex flex-wrap justify-content-between align-items-center bg-card/50 rounded-2xl p-4 border shadow-card mb-4 gap-2"
        >
          <!-- Filters -->
          <div class="d-flex flex-wrap align-items-center gap-y-3">
            <!-- Show Filter -->
            <div class="d-flex align-items-center gap-y-2">
              <label class="filter-label"
                >Show:</label
              >
              <select
                v-model="datatable.queries.per_page"
                class="border border-border round-xl px-2 py-1 text-sm bg-background"
                style="height: 2.5rem;"
                @change="getReviews"
              >
                <option :value="10">10</option>
                <option :value="15">15</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
                <option :value="999999">All</option>
              </select>
            </div>

            <!-- Rating Filter -->
            <div class="d-flex align-items-center gap-y-2">
              <label class="filter-label"
                >Rating&nbsp;Filter:</label
              >
              <select
                v-model="datatable.queries.review_rating"
                class="border border-border round-xl px-2 py-1 text-sm bg-background"
                style="height: 2.5rem;"
                @change="getReviews"
              >
                <option :value="0">All</option>
                <option :value="5">5 Star</option>
                <option :value="4">4 Star</option>
                <option :value="3">3 Star</option>
                <option :value="2">2 Star</option>
                <option :value="1">1 Star</option>
              </select>
            </div>

            <!-- Review Site Filter -->
            <div class="d-flex align-items-center gap-y-2">
              <label class="filter-label"
                >Review&nbsp;Site:</label
              >
              <select
                v-model="datatable.queries.review_site"
                class="border border-border round-xl px-2 py-1 text-sm bg-background review-site-select"
                style="height: 2.5rem;"
                @change="getReviews"
              >
                <option value="0">All</option>
                <option value="google">Google</option>
                <option value="facebook">Facebook</option>
                <option value="yelp">Yelp</option>
              </select>
            </div>
          </div>

          <!-- Actions -->
          <div class="d-flex flex-wrap align-items-center gap-y-3">
            <!-- Export Button -->
            <vue-json-to-csv :json-data="reviews" :csv-title="csvTitle()">
              <button class="btn btn-outline-primary export-btn round-xl" style="height: 2.5rem;">
                <i class="uil uil-download-alt"></i> Export
              </button>
            </vue-json-to-csv>

            <!-- Search Box -->
            <div class="position-relative search-box">
              <i class="uil uil-search search-icon text-muted"></i>
              <b-form-input
                v-model="datatable.queries.search"
                type="search"
                placeholder="Search..."
                class="form-control form-control-lg ps-5 round-xl search-input"
                style="height: 2.5rem; padding-left: 2.25rem"
              ></b-form-input>
            </div>
          </div>
        </div>
        <div class="bg-card/50 rounded-2xl p-4 border shadow-card mb-4">
          <h2 class="text-xl font-semibold text-foreground mb-4"
            >Reviews: {{ meta.business_title }}</h2
          >

          <div class="d-flex align-items-center flex-wrap">
            <!-- Platform -->
            <div class="d-flex align-items-center mr-4">
              <div class="h4 text-primary mx-2">Google</div>
              <span class="text-sm text-muted mx-4">Rating</span>
            </div>

            <!-- Rating -->
            <div class="d-flex align-items-center">
              <span class="h3 me-4 text-foreground">{{ meta.average }}</span>
              <div class="d-flex align-items-center mx-4">
                <i
                  class="uil uil-star text-warning"
                  style="font-size: 20px"
                ></i>
                <i
                  class="uil uil-star text-warning"
                  style="font-size: 20px"
                ></i>
                <i
                  class="uil uil-star text-warning"
                  style="font-size: 20px"
                ></i>
                <i
                  class="uil uil-star text-warning"
                  style="font-size: 20px"
                ></i>
                <i
                  class="uil uil-star text-warning"
                  style="font-size: 20px"
                ></i>
              </div>
              <span class="text-sm text-muted">{{ meta.total }} reviews</span>
            </div>
          </div>
        </div>
        <template v-if="!loadingReviews">
          <div class="table-responsive mb-0">
            <b-table
              :items="reviews"
              :fields="datatable.columns"
              :sort-by.sync="datatable.queries.sort_by"
              :sort-desc.sync="datatable.queries.sort_desc"
              :per-page="datatable.queries.per_page"
              data-export-types="['csv']"
              :no-local-sorting="true"
              thead-class="d-none"
              :borderless="true"
              @sort-changed="sortChanged"
            >
              <div class="text-center text-danger my-2">
                <b-spinner class="align-middle"></b-spinner>
              </div>
              <template v-slot:cell(review)="row">
                <ReviewCard :review="row.item" />
              </template>
            </b-table>
            <b-modal
              v-model="showSocialPostModal"
              :title="socialPostModalTitle"
            >
              <div v-if="postingItem">
                <p class="mb-3">{{ socialPostConfirmMessage(postingType) }}</p>
                <div
                  ><strong>Review Text: </strong>
                  <!-- eslint-disable-next-line -->
                  <span v-html="postingItem.review_text"></span
                ></div>
                <div
                  v-if="
                    postingType === 'instagram' && postingItem.instagram_post
                  "
                  class="mt-3"
                >
                  Posted to Instagram on
                  {{ postingItem.instagram_post.front_date }}<br />
                  Link:
                  <a
                    :href="
                      'https://www.instagram.com/p/' +
                      postingItem.instagram_post.ig_shortcode
                    "
                    target="_blank"
                    >https://www.instagram.com/p/{{
                      postingItem.instagram_post.ig_shortcode
                    }}</a
                  >
                </div>
                <div
                  v-if="postingType === 'facebook' && postingItem.facebook_post"
                  class="mt-3"
                >
                  Posted to Facebook on
                  {{ postingItem.facebook_post.front_date }}<br />
                  Link:
                  <a
                    :href="
                      'https://www.facebook.com/' +
                      postingItem.facebook_post.post_id
                    "
                    target="_blank"
                    >https://www.facebook.com/{{
                      postingItem.facebook_post.post_id
                    }}</a
                  >
                </div>
              </div>
              <template v-slot:modal-footer>
                <b-button variant="danger" @click="showSocialPostModal = false"
                  >Cancel</b-button
                >
                <b-button
                  variant="success"
                  @click="postSocial(postingItem, postingType)"
                  ><b-spinner v-if="postingItem.working" small /> Post</b-button
                >
              </template>
            </b-modal>
          </div>
          <div class="flex items-center justify-between mt-4 text-sm text-muted-foreground">
            <span>Total {{ totalReviews }} reviews</span>
            <div class="flex items-center space-x-2">
              <button
                class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                :disabled="datatable.queries.page === 1"
                @click="datatable.queries.page > 1 && (datatable.queries.page--, onPageChanged(datatable.queries.page))"
              >
                «
              </button>
              <div class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent hover:bg-secondary/80 request-review-btn" style="background-color: rgb(36, 99, 235); color: #ffffff;">
                {{ datatable.queries.page }}
              </div>
              <button
                class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                :disabled="datatable.queries.page >= Math.ceil(totalReviews / datatable.queries.per_page)"
                @click="datatable.queries.page < Math.ceil(totalReviews / datatable.queries.per_page) && (datatable.queries.page++, onPageChanged(datatable.queries.page))"
              >
                »
              </button>
            </div>
          </div>
        </template>
        <div v-else class="text-center">
          <b-spinner></b-spinner>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueJsonToCsv from 'vue-json-to-csv'
// import TypeWriter from '@components/Common/TypeWriter.vue'
import ReviewCard from './ReviewCard.vue'
import Vue from 'vue'
import {
  socialPostModalTitle,
  socialPostConfirmMessage,
} from '@utils/socialPostModal'
export default {
  components: {
    VueJsonToCsv,
    // TypeWriter,
    ReviewCard,
  },
  props: {
    defaultPerPage: {
      type: Number,
      default: 10,
    },
    forDashboard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      socialPostModalTitle,
      json_export_labels: {},
      datatable: {
        items: [],
        columns: [{ key: 'review' }],
        queries: {
          per_page: this.defaultPerPage,
          page: 1,
          status: 'active',
          review_rating: '0',
          review_site: '0', // Default to "All"
          search: null,
          sort_by: 'review_timestamp',
          sort_desc: true,
          location_reviews: true,
        },
      },
      showDeleteModal: false,
      selectedBusiness: null,
      loadingReviews: true,
      loadingImpersonate: false,
      loadingRemove: null,
      showWelcomeModal: false,
      showSocialPostModal: false,
      postingItem: null,
      postingType: '',
    }
  },

  computed: {
    reviews() {
      return this.$store.getters['reviews/all'] || []
    },

    totalReviews() {
      return this.$store.getters['reviews/total']
    },
    meta() {
      return this.$store.getters['reviews/meta']
    },
    reload() {
      return this.$store.getters['reviews/reload']
    },

    user() {
      return this.$store.getters['auth/user']
    },

    isAdmin() {
      return localStorage.getItem('main_access_role') === 'Admin'
    },
    socialImageExample() {
      return (
        (process.env.NODE_ENV === 'production'
          ? process.env.VUE_APP_BASE_API
          : process.env.VUE_APP_BASE_API_LOCAL) +
        `api/v1/sample-social-image?text=${encodeURI(
          this.postingItem.review_text
        )}&reviewer=${encodeURI(this.postingItem.author_title)}&business_name=${
          this.user.business.business_name
        }&business_phone=${this.user.business.phone_number}&icon=${
          this.postingItem.author_image
        }`
      )
    },
  },

  watch: {
    reload: (val) => {
      console.log(val)
    },
  },

  created() {
    this.$store.dispatch('reviews/setCurrentPage', 'index')
  },

  provide() {
    return {
      createReply: (review) => this.createReply(review),
      rewriteReply: (review) => this.rewriteReply(review),
      saveReply: (review) => this.saveReply(review),
      confirmPostSocial: (review, type) => this.confirmPostSocial(review, type),
    }
  },

  mounted() {
    this.getReviews()
    this.$store
      .dispatch('auth/getProfile')
      .then((user) => {
        // console.log(user);
      })
      .catch(() => {})
    // Force font size on selects and remove Bootstrap's custom-select classes
    this.$nextTick(() => {
      const selects = this.$el?.querySelectorAll('select.border.border-border, select.custom-select, select.custom-select-lg')
      if (selects) {
        selects.forEach((select) => {
          // Remove Bootstrap classes
          select.classList.remove('custom-select', 'custom-select-lg')
          // Force font size directly on element
          select.style.setProperty('font-size', '14px', 'important')
          select.style.setProperty('line-height', '1.5', 'important')
        })
      }
      // Also try after a delay to catch any late-rendered elements
      setTimeout(() => {
        const delayedSelects = this.$el?.querySelectorAll('select.border.border-border, select.custom-select, select.custom-select-lg')
        if (delayedSelects) {
          delayedSelects.forEach((select) => {
            select.classList.remove('custom-select', 'custom-select-lg')
            select.style.setProperty('font-size', '14px', 'important')
            select.style.setProperty('line-height', '1.5', 'important')
          })
        }
      }, 100)
    })
  },

  methods: {
    socialPostConfirmMessage,
    confirmPostSocial(review, type) {
      if (!this.meta.can_social_post) {
        Vue.$toast.open({
          message: 'Sorry, you have reached your post limit.',
          type: 'error',
          duration: 5000,
        })
        return
      }
      this.postingItem = review
      this.postingType = type
      this.showSocialPostModal = true
    },
    postSocial(review, type) {
      review.working = true
      this.$store
        .dispatch('reviews/postSocial', {
          type: type,
          review_id: review.id,
          user_id: this.user.id,
          business_id: this.user.business.id,
        })
        .then((res) => {
          review.facebook_post = res.facebook_post
          review.instagram_post = res.instagram_post
          review.gbp_post = res.gbp_post
          review.working = false
          this.showSocialPostModal = false
        })
        .catch((res) => {
          review.working = false
          console.log(res)
        })
    },
    resendEmail(review) {
      review.working = true
      this.$store
        .dispatch('reviews/resendEmail', {
          review_id: review.id,
          user_id: this.user.id,
        })
        .then((res) => {
          if (res.reply !== '') {
            review.reply = res.reply
          }
          review.working = false
        })
        .catch((res) => {
          review.working = false
          console.log(res)
        })
    },
    csvTitle() {
      return 'showmyrep-reviews' + Date.now()
    },
    sortChanged(args) {
      this.datatable.queries.sort_by = args.sortBy
      this.datatable.queries.sort_desc = args.sortDesc
      this.datatable.queries.page = 1
      this.getReviews()
    },

    onPageChanged(page) {
      this.datatable.queries.page = page
      this.getReviews()
    },

    getReviews() {
      this.loadingReviews = true
      this.$store
        .dispatch('reviews/getAll', {
          queries: this.datatable.queries,
          page: 'index',
          businessId: this.user.business?.id,
        })
        .then((res) => {
          this.business = res.business
          this.loadingReviews = false
        })
        .catch(() => {
          this.loadingReviews = false
        })
    },
    createReply(review) {
      if (!this.meta.can_reply_engine) {
        Vue.$toast.open({
          message: 'Sorry, you have reached your reply limit.',
          type: 'error',
          duration: 5000,
        })
        return
      }
      review.working = true
      review.writing_reply = true
      review.waiting_4_ai = true
      review.stopped_typing = false
      review.openai_error = null
      review.editing = false
      this.$store
        .dispatch('reviews/getReply', {
          review: review.review_text,
          reviewers_name: review.author_title,
          review_stars: review.review_rating + ' out of 5 stars. ',
          business: this.user?.business?.id,
        })
        .then((res) => {
          console.log(res.data)
          review.reply = res.data.reply
          review.working = false
          review.waiting_4_ai = false
          if (res.data.error) {
            review.openai_error = res.data.error
          }
        })
        .catch(() => {
          review.working = false
          review.writing_reply = false
          review.waiting_4_ai = false
          review.stopped_typing = true
        })
    },
    rewriteReply(review) {
      if (!this.meta.can_reply_engine) {
        Vue.$toast.open({
          message: 'Sorry, you have reached your reply limit.',
          type: 'error',
          duration: 5000,
        })
        return
      }
      review.working = true
      review.writing_reply = true
      review.waiting_4_ai = true
      review.stopped_typing = false
      review.openai_error = null
      review.editing = false
      this.$store
        .dispatch('reviews/rewriteReply', {
          review: review.review_text,
          reviewers_name: review.author_title,
          review_stars: review.review_rating + ' out of 5 stars. ',
          business: this.user?.business?.id,
        })
        .then((res) => {
          console.log(res.data)
          review.reply = res.data.reply
          review.working = false
          review.waiting_4_ai = false
          if (res.data.error) {
            review.openai_error = res.data.error
          }
        })
        .catch(() => {
          review.working = false
          review.writing_reply = false
          review.waiting_4_ai = false
          review.stopped_typing = true
        })
    },
    saveReply(review) {
      review.working = true
      this.$store
        .dispatch('reviews/saveReply', {
          review: review,
        })
        .then((res) => {
          // res is ReviewResource from API (see OpenAiController::saveReply)
          review.reply = res.reply
          review.reply_updated = res.reply_updated
          // Keep a convenience field for UI; ReviewCard will also derive from reply_updated
          review.repliedAt = res.reply_updated
          review.working = false
          review.editing = false
        })
        .catch(() => {
          review.working = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.status-filter {
  text-transform: capitalize;
}

.filter-label {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 0;
  line-height: 1.5;
}

// Override Bootstrap's custom-select classes to ensure 14px font size
// Target all possible combinations with maximum specificity
.custom-select,
.custom-select-lg,
select.custom-select,
select.custom-select-lg,
.border.border-border.custom-select,
.border.border-border.custom-select-lg,
select.border.border-border.custom-select,
select.border.border-border.custom-select-lg {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

// Ensure our native selects maintain 14px font size - highest specificity
select.border.border-border,
select.border.border-border.custom-select,
select.border.border-border.custom-select-lg {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

.search-box {
  .form-control {
    font-size: 14px !important;
    line-height: 1.5 !important;
  }
}

// Ensure all filter selects have consistent height and border-radius
select.border.border-border,
.review-site-select {
  height: 2.5rem !important;
  display: flex;
  align-items: center;
  border-radius: 0.75rem !important;
}

// Ensure export button matches select height and border-radius
.export-btn {
  height: 2.5rem !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem !important;
}

// Ensure search input matches select height and border-radius
.search-input {
  height: 2.5rem !important;
  border-radius: 0.75rem !important;
}
</style>

<style lang="scss">
// Global styles to override Bootstrap's custom-select-lg font-size (1.09375rem)
// Must be global (not scoped) to override Bootstrap's styles
.custom-select-lg,
select.custom-select-lg,
.border.border-border.custom-select-lg,
select.border.border-border.custom-select-lg {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

.custom-select,
select.custom-select,
.border.border-border.custom-select,
select.border.border-border.custom-select {
  font-size: 14px !important;
  line-height: 1.5 !important;
}

// Target selects with our classes
select.border.border-border {
  font-size: 14px !important;
  line-height: 1.5 !important;
}
</style>

